import { useEffect, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { BattleStartedPayload } from "@/types/battle"
import { formatPointsDelta } from "@/constants/game.constants"
import { useDuelMachine } from "@/features/duel/useDuelMachine"
import AbilityPicker from "@/features/duel/components/AbilityPicker"
import RevealSequence from "@/features/duel/components/RevealSequence"
import CardPicker from "@/features/duel/components/CardPicker"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function DuelPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const initial = (location.state as { battle?: BattleStartedPayload } | null)?.battle
  const { refreshPassport, bumpCards, passport } = useGuardiansPassport()
  const [selectedAbilityId, setSelectedAbilityId] = useState<string | null>(null)
  const { state, mappedResult, selectCard, reset, revealDone } =
    useDuelMachine(initial)

  const guardianRank =
    passport?.guardianRankInfo?.rank ?? passport?.guardianRank ?? 1

  useEffect(() => {
    setSelectedAbilityId(null)
  }, [state.battle?.battleId])

  useEffect(() => {
    if (state.phase === "result") {
      void refreshPassport()
      bumpCards()
    }
  }, [state.phase, refreshPassport, bumpCards])

  const battle = state.battle
  const opponent = battle?.opponent?.nickname ?? "Соперник"

  return (
    <GameScreenLayout title="Дуэль">
      {!battle ? (
        <p className="text-sm text-guardians-muted">
          Нет активного боя.{" "}
          <Link to="/play/matchmaking" className="text-guardians-accent">
            Радар
          </Link>
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between text-sm text-guardians-muted">
            <span>vs {opponent}</span>
            <span>
              {state.phase === "selecting" && `⏱ ${state.timerSec}s`}
            </span>
          </div>
          <p className="text-xs uppercase tracking-wide text-guardians-muted">
            {state.phase}
          </p>

          {state.phase === "selecting" && !state.yourCard && (
            <>
            <AbilityPicker
              affiliationElement={passport?.affiliationElement}
              guardianRank={guardianRank}
              selectedAbilityId={selectedAbilityId}
              onSelect={setSelectedAbilityId}
            />
            <CardPicker
              cards={battle.yourCards}
              onSelect={(card) => selectCard(card.id, selectedAbilityId)}
            />
            </>
          )}

          {state.yourCard && state.phase !== "result" && state.phase !== "revealing" && (
            <p className="text-sm text-guardians-accent">Карта выбрана — ждём соперника…</p>
          )}

          {state.phase === "revealing" && mappedResult ? (
            <RevealSequence
              yourSide={mappedResult.yourSide}
              opponentSide={mappedResult.opponentSide}
              onComplete={revealDone}
            />
          ) : null}

          {state.phase === "result" && mappedResult && (
            <div
              className={clsx(
                "rounded-xl border p-4 text-center",
                mappedResult.yourSide.outcome === "WIN"
                  ? "border-green-500/40"
                  : mappedResult.yourSide.outcome === "LOSE"
                    ? "border-red-500/40"
                    : "border-white/20"
              )}
            >
              <p className="text-lg font-semibold">{mappedResult.yourSide.outcome}</p>
              <p className="mt-1 text-sm text-guardians-muted">
                {formatPointsDelta(mappedResult.yourSide.ptsDelta ?? 0)}
              </p>
              <button
                type="button"
                className="mt-4 w-full rounded-xl border border-white/20 py-2 text-sm"
                onClick={() => {
                  reset()
                  navigate("/play")
                }}
              >
                На главную
              </button>
            </div>
          )}

          {state.phase === "cancelled" && (
            <div className="text-sm text-red-300">
              {state.cancelled?.message ?? "Бой отменён"}
              <button
                type="button"
                className="mt-3 block w-full rounded-xl border border-white/20 py-2"
                onClick={() => navigate("/play/matchmaking")}
              >
                В радар
              </button>
            </div>
          )}
        </div>
      )}
    </GameScreenLayout>
  )
}
